"use client";
import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import Placeholder from "@tiptap/extension-placeholder";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
};

export default function TiptapEditor({ value, onChange, placeholder = "Type something..." }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Color,
      Placeholder.configure({
        placeholder,
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
      }),
    ],
    content: value || "", // ⬅️ WAJIB
    editorProps: {
      attributes: {
        class:
          "ProseMirror min-h-[160px] w-full border rounded-b-lg p-3 focus:outline-none bg-white",
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // ⬇️ Prevent reset saat mengetik
  useEffect(() => {
    if (!editor) return;

    // Hanya set content jika value berbeda
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);



  return (
    <div className="w-full border rounded-lg">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 border-b bg-gray-50 p-2 rounded-t-lg">
        <button
          onClick={() => editor?.chain().focus().toggleBold().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("bold") ? "bg-gray-300" : ""}`}
        >
          B
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("italic") ? "bg-gray-300" : ""}`}
        >
          I
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("underline") ? "bg-gray-300" : ""}`}
        >
          U
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleStrike().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("strike") ? "bg-gray-300" : ""}`}
        >
          S
        </button>

        {/* <button
          onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`px-2 py-1 rounded ${editor?.isActive("heading", { level: 1 }) ? "bg-gray-300" : ""}`}
        >
          H1
        </button> */}

        <button
          onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`px-2 py-1 rounded ${editor?.isActive("heading", { level: 2 }) ? "bg-gray-300" : ""}`}
        >
          H2
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`px-2 py-1 rounded ${editor?.isActive("heading", { level: 3 }) ? "bg-gray-300" : ""}`}
        >
          H3
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("bulletList") ? "bg-gray-300" : ""}`}
        >
          • List
        </button>

        <button
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("orderedList") ? "bg-gray-300" : ""}`}
        >
          1. List
        </button>

        {/* <button
          onClick={() => editor?.chain().focus().toggleBlockquote().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("blockquote") ? "bg-gray-300" : ""}`}
        >
          ❝ ❞
        </button> */}

        {/* <button
          onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
          className={`px-2 py-1 rounded ${editor?.isActive("codeBlock") ? "bg-gray-300" : ""}`}
        >
          {"</>"}
        </button>

        <button
          onClick={() => {
            const url = window.prompt("Enter link URL");
            if (url) editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
          }}
          className={`px-2 py-1 rounded ${editor?.isActive("link") ? "bg-gray-300" : ""}`}
        >
          🔗
        </button> */}

        <button onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()} className="px-2 py-1 rounded">
          Clear
        </button>

        <button onClick={() => editor?.chain().focus().undo().run()} className="px-2 py-1 rounded">
          Undo
        </button>

        <button onClick={() => editor?.chain().focus().redo().run()} className="px-2 py-1 rounded">
          Redo
        </button>
      </div>

      {/* Editor */}
      <EditorContent editor={editor} />
    </div>
  );
}
